import { type MessageMedia as MediaItem } from "@/worker/schemas";

interface MessageMediaProps {
  media: MediaItem[];
  isArabic: boolean;
}

/**
 * The agent's attached photos on one reply (PLT-1783). Tailwind twin of the
 * embeddable widget's `src/widget/components/MessageMedia.tsx` — same rules
 * (one image fills the bubble, two or more scroll horizontally, a document
 * renders as a link), expressed in this app's own utility classes.
 */
export function MessageMedia({ media, isArabic }: MessageMediaProps) {
  if (media.length === 0) return null;

  const openLabel = isArabic ? "فتح المرفق" : "Open attachment";
  const single = media.length === 1;

  return (
    // -mx-2 pulls the strip toward the bubble's edges so the photos are not
    // double-padded. Scroll direction is inherited: this app's bubbles follow
    // the page `dir`, so an Arabic page scrolls the strip right-to-left. (The
    // embeddable widget's twin does NOT, because its history container is
    // pinned `direction: ltr`; each follows its own container.)
    <div
      className={`-mx-2 mb-2 flex gap-2 overflow-x-auto px-2 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
        single ? "" : "snap-x snap-mandatory"
      }`}
    >
      {media.map((item, index) => {
        const key = item.assetId || `${item.url}-${index}`;

        if (item.type === "document") {
          return (
            <a
              key={key}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-none rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 no-underline hover:bg-gray-200"
            >
              {item.fileName || item.caption || openLabel}
            </a>
          );
        }

        return (
          <figure
            key={key}
            className={`m-0 flex-none snap-start ${single ? "w-full" : "w-40"}`}
          >
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              <img
                src={item.url}
                // Never empty: an unlabelled photo in a text thread is opaque
                // to a screen reader, and the caption is what the model wrote
                // to describe it.
                alt={item.altText || item.caption || openLabel}
                loading="lazy"
                className={`block w-full rounded-lg bg-gray-100 object-cover ${
                  single ? "max-h-56" : "h-32"
                }`}
              />
            </a>
            {item.caption && (
              <figcaption className="mt-1 text-xs leading-snug text-gray-500">
                {item.caption}
              </figcaption>
            )}
          </figure>
        );
      })}
    </div>
  );
}
